import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Button, DateField } from '@structural-climbing/ui'
import { useGymTimezone } from '../../contexts/GymContext'
import { gymLocalDate } from '../../lib/gymLocalDate'
import type { ProfileSubscriptionData } from './ProfileSubscription'

/** `YYYY-MM-DD` plus whole days, on the calendar. */
function addDays(day: string, days: number): string {
  const d = new Date(`${day}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

/** Calendar days from `from` to `to`, both counted. */
function daysInclusive(from: string, to: string): number {
  const ms = new Date(`${to}T12:00:00Z`).getTime() - new Date(`${from}T12:00:00Z`).getTime()
  return Math.round(ms / 86_400_000) + 1
}

function formatDay(day: string): string {
  return new Date(`${day}T12:00:00Z`).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

interface Props {
  sub: ProfileSubscriptionData
  /** Freeze picks the days; unfreeze only confirms. */
  mode: 'freeze' | 'unfreeze'
  onDone: () => void
  onClose: () => void
}

/**
 * Freezes the member's plan for a stretch of days, or ends a freeze. The Worker
 * owns the arithmetic (core-flows rule 11); the end date shown here is only a
 * preview of what it will write.
 */
export function FreezeSubscriptionModal({ sub, mode, onDone, onClose }: Props) {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const timezone = useGymTimezone()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const today = gymLocalDate(new Date(), timezone)
  const planStart = sub.startsAt ? gymLocalDate(new Date(sub.startsAt), timezone) : today
  const firstAllowed = planStart > today ? planStart : today
  const lastDay = sub.expiresAt ? gymLocalDate(new Date(sub.expiresAt), timezone) : null

  const [from, setFrom] = useState(firstAllowed)
  const [to, setTo] = useState(firstAllowed)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const valid = mode === 'unfreeze' || (!!from && !!to && from >= firstAllowed && to >= from)
  const newEnd =
    valid && lastDay && mode === 'freeze' ? addDays(lastDay, daysInclusive(from, to)) : null

  async function submit() {
    setSaving(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/subscriptions/${encodeURIComponent(sub.id)}/freeze`, {
        method: mode === 'freeze' ? 'POST' : 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        ...(mode === 'freeze' ? { body: JSON.stringify({ from, to }) } : {}),
      })
      if (!res.ok) {
        setError(t(mode === 'freeze' ? 'members.freezeError' : 'members.unfreezeError'))
        return
      }
      onDone()
    } catch {
      setError(t(mode === 'freeze' ? 'members.freezeError' : 'members.unfreezeError'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-label={t(mode === 'freeze' ? 'members.freezeTitle' : 'members.unfreezeTitle')}
        className="w-full max-w-md overflow-y-auto rounded-xl bg-content1 p-6 shadow-xl"
        style={{ maxHeight: '90vh' }}
      >
        <div className="mb-5">
          <h2 className="text-base font-semibold">
            {t(mode === 'freeze' ? 'members.freezeTitle' : 'members.unfreezeTitle')}
          </h2>
          <p className="mt-1 text-sm text-foreground/60">
            {t(mode === 'freeze' ? 'members.freezeHint' : 'members.unfreezeBody')}
          </p>
        </div>

        {mode === 'freeze' && (
          <div className="mb-4 grid gap-3 sm:grid-cols-2">
            <DateField
              label={t('members.freezeFrom')}
              value={from}
              min={firstAllowed}
              max={lastDay ?? undefined}
              onValueChange={(v) => {
                setFrom(v)
                if (v && to < v) setTo(v)
              }}
            />
            <DateField
              label={t('members.freezeTo')}
              value={to}
              min={from || firstAllowed}
              onValueChange={setTo}
            />
            {newEnd && (
              <p className="text-sm text-foreground/60 sm:col-span-2">
                {t('members.freezeNewEnd', { date: formatDay(newEnd) })}
              </p>
            )}
          </div>
        )}

        {error && <p className="mb-3 text-sm text-danger">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="flat" isDisabled={saving} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            size="sm"
            color="primary"
            isLoading={saving}
            isDisabled={!valid}
            onClick={() => void submit()}
          >
            {t(mode === 'freeze' ? 'members.freeze' : 'members.unfreeze')}
          </Button>
        </div>
      </div>
    </div>
  )
}
